import { oc } from '@orpc/contract';
import { ACCOUNT_STATUSES } from '@lsc/domain';
import { z } from 'zod';
import { accountGroupRoutes } from './identity.js';
import { accountGet } from './account-detail.js';
import { accountResolve } from './resolve.js';
/** What the query matched on, so the row can say why it is here. */
export const MATCHED_ON = ['phone', 'mid', 'name', 'contact', 'owner', 'email'];
export const accountSearchInput = z.object({
    /** Phone, MID, DBA, legal name, contact or owner name, email. The classifier picks the branches. */
    q: z.string().trim().min(1).max(120),
    /** Closed and cancellation_requested are hidden by default but never silently: `hiddenInactive` says how many. */
    includeInactive: z.boolean().default(false),
});
export const accountHit = z.object({
    accountId: z.uuid(),
    /** DBA with legal-name fallback; never empty (MID, then id, as a last resort). */
    label: z.string(),
    legalName: z.string().nullable(),
    legalEntityId: z.uuid().nullable(),
    /** `vivid` | `green_payments` | `epi` */
    processor: z.string(),
    env: z.string(),
    mid: z.string().nullable(),
    status: z.enum(ACCOUNT_STATUSES),
    csPhone: z.string().nullable(),
    csEmail: z.string().nullable(),
    city: z.string().nullable(),
    zip: z.string().nullable(),
    boardedOn: z.string().nullable(),
    /** Which branches hit this account. */
    matchedOn: z.array(z.enum(MATCHED_ON)),
    /** The thing that matched when it is not the account itself: a contact or owner name, an email, a MID. */
    matchDetail: z.string().nullable(),
});
export const accountSearchOutput = z.object({
    rows: z.array(accountHit),
    /** Matches after the inactive filter. Never truncated silently: `rows.length < total` means `truncated`. */
    total: z.number().int(),
    truncated: z.boolean(),
    /** Inactive matches left out because `includeInactive` was false. 0 when it was true. */
    hiddenInactive: z.number().int(),
    /** How the classifier read the query; the desk shows it so a rep knows why a result is or is not there. */
    queryKind: z.enum(['empty', 'phone', 'ambiguous', 'account']),
});
/**
 * The account's communication, two sides never mixed (spec 2026-11-02-account-communications-design.md):
 * external is what the merchant said and what we said to them (texts, calls, voicemails); internal
 * is the team's notes and changes on the account's tickets. Newest first, keyset by time.
 */
export const communicationsInput = z.object({ accountId: z.uuid(), side: z.enum(['external', 'internal']), /** `owner`: External across every location under the account's legal entity (a chain owner's calls, 2026-11-03). */ scope: z.enum(['account', 'owner']).default('account'), cursor: z.string().max(80).nullable().optional(), limit: z.number().int().min(1).max(100).default(50) });
export const communicationItem = z.object({
    /** `<kind>:<id>`, unique on the account. */
    key: z.string(),
    at: z.string(),
    kind: z.enum(['text', 'call', 'voicemail', 'note', 'shift_note', 'status', 'stage', 'assign', 'summary']),
    /** `in`: the merchant to us; `out`: us to the merchant; `team`: among ourselves. */
    direction: z.enum(['in', 'out', 'team']),
    /** Who, as the desk shows names: a rep, or the merchant's number. */
    who: z.string().nullable(),
    /** The words: the text, the recap, the note, the change. Already redacted where it came from outside. */
    text: z.string().nullable(),
    /** Delivery or outcome words: "delivered", "undelivered", "missed", "5:56". */
    status: z.string().nullable(),
    rootCallId: z.string().nullable(),
    hasTranscript: z.boolean(),
    ticket: z.object({ id: z.string(), number: z.string() }).nullable(),
    mediaUrl: z.string().nullable(),
    /** The words came through Slack (the list mirror or a thread reply): the desk renders Slack markup. */
    fromSlack: z.boolean().default(false),
    /** The store the row belongs to, for the owner-wide view; the account's own name otherwise. */
    location: z.string().nullable().default(null),
});
export const communicationsOutput = z.object({ items: z.array(communicationItem), total: z.number().int(), nextCursor: z.string().nullable(), counts: z.object({ external: z.number().int(), internal: z.number().int() }), slackNames: z.record(z.string(), z.string()).default({}) });
export const accountsContract = {
    ...accountGroupRoutes,
    communications: oc.route({ method: 'POST', path: '/accounts/communications', summary: "The account's communication, external or internal, newest first" }).input(communicationsInput).output(communicationsOutput),
    resolve: accountResolve,
    get: accountGet,
    search: oc.route({ method: 'POST', path: '/accounts/search', summary: 'Find accounts by phone, MID, name, contact or email' }).input(accountSearchInput).output(accountSearchOutput),
};
//# sourceMappingURL=accounts.js.map